import { useAppSelector } from "@/store/hooks"
import classNames from "classnames"
import PlayerListItem2 from "./PlayerListItem2"
import React, { useMemo } from "react"
import { getUserCreds } from "@/utils/utils"
import { getSeatArrangement } from "@/utils/seating"

interface PlayersListProps {
  sidebar: "left" | "right"
  situationPlayers: IPlayer[]
}

const PlayersList: React.FC<PlayersListProps> = ({
  sidebar,
  situationPlayers,
}) => {
  const gamePlayers = useAppSelector((state) => state.global.myGame?.players)
  // A fast-pace vote takes over the strip above the topmost card on the left
  // (see SidebarLeft), and needs more of it than the strip normally has. Read
  // here rather than passed in from that one sidebar on purpose: both columns
  // have to give up the same height at the same moment or the row-matching
  // below comes apart, and the right column simply leaves its strip empty.
  const fastPaceVoteOpen = useAppSelector(
    (state) => !!state.global.myGame?.situation?.fastPaceVote
  )
  const myEmail = getUserCreds().email
  // Same input on both sides, so the two sidebars always agree on who sits where.
  const seats = useMemo(
    () => getSeatArrangement(gamePlayers, myEmail),
    [gamePlayers, myEmail]
  )
  const columnPlayers = sidebar === "left" ? seats.left : seats.right
  const myCard = sidebar === "left" ? seats.me : undefined

  // Both sidebars lay their cards out on the *same* number of rows — the taller
  // of the two columns — so row 1 on the left is row 1 on the right and so on,
  // whatever the player count. Your own card counts as a row of the left column
  // (it's pinned to the bottom of it), and a column with fewer players simply
  // leaves its top rows empty rather than sliding everything up a row, which is
  // what used to knock the two lists out of step.
  const rows: (IUser | null)[] = [...columnPlayers, ...(myCard ? [myCard] : [])]
  const rowCount = Math.max(
    seats.left.length + (seats.me ? 1 : 0),
    seats.right.length,
    1
  )
  // Pad at the top: seats are handed out bottom-up (see utils/seating.ts), so
  // the spare rows belong above the cards, not below them.
  const paddedRows: (IUser | null)[] = [
    ...new Array(Math.max(rowCount - rows.length, 0)).fill(null),
    ...rows,
  ]

  const renderPlayer = (user: IUser) => (
    <PlayerListItem2
      key={user.email}
      player={situationPlayers?.find((x) => x.id === user.email) as IPlayer}
      user={user}
    ></PlayerListItem2>
  )

  return (
    // Equal-height rows spread over the whole column, rather than a stack on a
    // fixed gap: the cards then use all the room the sidebar has instead of
    // bunching at one end, and because every row is the same fraction of the
    // same box on both sides, matching rows sit at matching heights for free.
    // mt-40 keeps the first card's "your turn" badge (which floats 4rem above
    // the card) clear of the leave/pause/settings/music buttons at top-6, and
    // is the gap you see above the topmost card. On a desktop it widens to
    // 16.5rem for the fast-pace vote panel, which parks in that gap on the left
    // and needs the room — the cards give up the difference for the 45 seconds
    // the vote is open rather than having something dropped on top of them, and
    // the transition is what makes the column read as making room. flex-1 + the
    // matching h-[30vh] bottom slot in both sidebars is what gives the two
    // lists the same box.
    <div
      className={classNames(
        // mb-8 is the band of air under the lowest card, between it and
        // whatever the sidebar parks below the list — the auction/trade keys on
        // the left, the trades panel on the right. It sits here, on the list
        // both columns render, rather than in one sidebar's bottom slot: the
        // rows only line up because the two lists are handed the identical box,
        // so a margin on one side alone would slide that side out of step.
        "grid w-full flex-1 min-h-0 mb-8 transition-[margin-top] duration-300 ease-out",
        // Desktop only. A phone has far less column to give: shifting the
        // cards down there costs more than the overlap it avoids, and the
        // whole list visibly lurching mid-turn is worse than the panel sitting
        // over the top of it for 45 seconds. So `mobile:` pins the strip back
        // to its closed height and the panel simply overlaps — screens are
        // last in tailwind.config.js, so this wins against the base class.
        fastPaceVoteOpen ? "mt-[16.5rem] mobile:mt-40" : "mt-40"
      )}
      style={{ gridTemplateRows: `repeat(${rowCount}, minmax(0, 1fr))` }}
    >
      {paddedRows.map((user, index) =>
        user ? (
          <div
            key={user.email}
            // The top row hugs the top of the column and every other row hugs
            // the bottom of its own, which spreads the cards to the far edges
            // while keeping the last one — your own card on the left — flush
            // with the trade panel/bank slot below. The rule depends only on
            // the row index, so it lands identically on both sides. A lone row
            // has nothing to spread against and stays at the bottom, next to
            // the seat that plays after you.
            className={classNames("flex w-full justify-end", {
              "self-start": index === 0 && rowCount > 1,
              "self-end": !(index === 0 && rowCount > 1),
            })}
            data-testid={user === myCard ? "game.my-player-card" : undefined}
          >
            {renderPlayer(user)}
          </div>
        ) : (
          <div key={`empty-seat-${index}`}></div>
        )
      )}
    </div>
  )
}

export default React.memo(PlayersList)
